import { isEqual, omit } from 'lodash';
import type { Action } from 'redux';
import type { CardData, CardEditorType, StoryModel } from 'types/story';
import { StoryFacade } from 'utils/facades/story-facade';
import type { AdminThunkAction } from 'admin/reducers';
import { selectEditableStateInfo } from 'admin/reducers/card-editor/selectors';
import { bindEditorWrites } from 'admin/components/pages/Story/StoryEditorChat/bind-editor-writes';
import type { FullEditableCardDataPayload } from 'admin/components/pages/Story/StoryEditorChat/types';
import { injectContentHeight } from './update-editable-card-data';

type EditedCardRefreshPlan =
	| { status: 'missing' }
	| { status: 'unchanged' }
	/** Only the screenshots in the story settings differ: written without an undo step. */
	| { status: 'screenshots'; payload: FullEditableCardDataPayload }
	/** What undo restores differs: written as an undo step. */
	| { status: 'changed'; payload: FullEditableCardDataPayload };

export type EditedCardRefreshStatus = EditedCardRefreshPlan['status'];

type CardEditorPresent = FullEditableCardDataPayload['value'];

/** What the card editor's undo history restores; the screenshots are a record of the story, not an edit of it. */
const isUndoRestoredStateEqual = (a: CardEditorPresent, b: CardEditorPresent) =>
	isEqual(a.data, b.data) &&
	isEqual(a.storyElements, b.storyElements) &&
	isEqual(a.symbols, b.symbols) &&
	isEqual(omit(a.storySettings, 'screenshot'), omit(b.storySettings, 'screenshot'));

/** The edited card as `story` has it, in the card editor's shape; null when the card no longer exists. */
export function editedCardFromStory(story: StoryModel, cardId: string): CardEditorPresent | null {
	const { latest } = story.storyVersions;
	const { card } = StoryFacade.findCardInSteps(cardId, latest.data.steps);
	if (!card) return null;
	return {
		data: omit(card, 'index'),
		storySettings: latest.settings,
		storyElements: latest.data.elements,
		symbols: latest.data.symbols ?? {},
	};
}

/**
 * What writing `next` to the card editor changes: nothing when the editor already shows it,
 * and no undo step when only the screenshots differ, so a catch-up leaves no undo step that restores nothing.
 */
export function planEditedCardRefresh(next: CardEditorPresent, current: CardEditorType): EditedCardRefreshPlan {
	const shown: CardEditorPresent = {
		data: omit(current.data, 'index') as CardData,
		storySettings: current.storySettings,
		storyElements: current.storyElements,
		symbols: current.symbols,
	};
	if (!isUndoRestoredStateEqual(next, shown)) {
		return { status: 'changed', payload: { type: 'full', disableSync: true, value: next } };
	}
	if (!isEqual(next.storySettings, shown.storySettings)) {
		return {
			status: 'screenshots',
			payload: { type: 'full', disableSync: true, excludeFromUndo: true, value: next },
		};
	}
	return { status: 'unchanged' };
}

/** Refreshes the open card from `story`, without autosave; resolves with what it found. */
export function refreshEditedCard(
	story: StoryModel,
	cardId: string
): AdminThunkAction<Action, EditedCardRefreshStatus> {
	return async (dispatch, getState) => {
		const current = getState().cardEditor.present;
		const next = editedCardFromStory(story, cardId);
		if (!next) return 'missing';

		// Every card editor write carries the content height measured in the preview; the plan compares what
		// the write will show, or a stale saved height would make every catch-up a card change.
		const measured = injectContentHeight(current, { type: 'full', value: next }) as FullEditableCardDataPayload;
		const plan = planEditedCardRefresh(measured.value, current);
		if (plan.status === 'changed' || plan.status === 'screenshots') {
			bindEditorWrites(dispatch, () => selectEditableStateInfo(getState())).updateEditableCardData(plan.payload);
		}
		return plan.status;
	};
}
